import { Modal } from "ant-design-vue"

import { i18n } from "@/i18n"
import reportError from "@/utils/reportError"

export const CHUNK_RELOAD_QUERY = "_station_reload"

export function isChunkLoadError(error) {
  const message = String(error?.message || error || "")
  return (
    error?.name === "ChunkLoadError" ||
    error?.code === "CSS_CHUNK_LOAD_FAILED" ||
    /ChunkLoadError|Loading (?:CSS )?chunk .+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(message)
  )
}

// Static Ant Design modals also work when a bootstrap import fails before Vue mounts.
function showRetry(retry) {
  return Modal.error({
    title: i18n.global.t("global.errors.generic.title"),
    content: i18n.global.t("global.errors.generic.body"),
    okText: i18n.global.t("adBlockDetection.actions.refresh"),
    centered: true,
    maskClosable: false,
    keyboard: false,
    onOk: retry
  })
}

export function installChunkRecovery(router, { browser = window, showRetryPrompt = showRetry } = {}) {
  // Keeping the attempt in the URL survives reloads even when Safari storage is unavailable.
  let attempted = new URL(browser.location.href).searchParams.has(CHUNK_RELOAD_QUERY)
  let destination = browser.location.href
  let reloadTimer = null
  let retryPrompt = null

  const reload = () => {
    const url = new URL(destination, browser.location.href)
    url.searchParams.set(CHUNK_RELOAD_QUERY, `${Date.now()}`)
    browser.location.replace(url.href)
  }

  const handleError = (error, source, to) => {
    if (!isChunkLoadError(error)) return
    if (to) destination = router.resolve(to).href
    else if (reloadTimer === null && !retryPrompt) destination = browser.location.href
    if (reloadTimer !== null || retryPrompt) return

    reportError(error, { context: "chunkRecovery", source, automaticReloadAttempted: attempted })
    if (attempted || browser.navigator.onLine === false) {
      retryPrompt = showRetryPrompt(reload)
      return
    }

    attempted = true
    // Coalesce router/window reports so the failed route can supply the destination.
    reloadTimer = browser.setTimeout(reload, 0)
  }

  const removeRouterError = router.onError((error, to) => handleError(error, "router", to))
  const removeAfterEach = router.afterEach((to, from, failure) => {
    if (failure || reloadTimer !== null || retryPrompt) return
    attempted = false
    if (Object.prototype.hasOwnProperty.call(to.query, CHUNK_RELOAD_QUERY)) {
      const query = { ...to.query }
      delete query[CHUNK_RELOAD_QUERY]
      router.replace({ path: to.path, query, hash: to.hash }).catch((error) => {
        reportError(error, { context: "chunkRecovery.cleanUrl" })
      })
    }
  })

  const onWindowError = (event) => handleError(event.error ?? { message: event.message }, "window.error")
  const onRejection = (event) => handleError(event.reason, "window.unhandledrejection")
  browser.addEventListener("error", onWindowError)
  browser.addEventListener("unhandledrejection", onRejection)

  return () => {
    removeRouterError()
    removeAfterEach()
    browser.removeEventListener("error", onWindowError)
    browser.removeEventListener("unhandledrejection", onRejection)
    if (reloadTimer !== null) browser.clearTimeout(reloadTimer)
    retryPrompt?.destroy()
  }
}
